import GlobalStyles from '@mui/material/GlobalStyles';
import {
  memo,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { radii } from '../../theme/tokens';
import { chartStatusDarkenCoefficient, uiTokens } from '../../theme/uiTokens';
import { chartBarSizing } from '../theme/chartBarSizing';
import { guidedYAxisTickNumber } from './barChartAxisScale';
import { Box } from './Box';
import { formatChartDateLabel } from './chartDateLabel';
import { resolveChartPlotAreaBackgroundColor } from './chartPlotAreaBackgroundColor';
import { buildChartPlotAreaFillSx } from './chartPlotAreaLayers';
import { calculateChartPlotBounds } from './chartPlotBounds';
import { buildAdaptiveTickLabelInterval } from './chartTickLabelInterval';
import { darken } from './colorUtils';
import { useTheme } from './hooks';
import { type StackedBarChartProps, type StackedBarSeries } from './StackedBarChart.types';
import { buildStackedBarChartAxisLineSx } from './stackedBarChartAxisLineSx';
import { formatStackedBarTooltipValue, formatWholeNumber } from './stackedBarChartFormatters';
import {
  buildEstimatedStackedBarChartPlotBounds,
  type StackedBarChartPlotBounds,
} from './stackedBarChartPlotBounds';
import { StackedBarChartRoundedTopBar } from './StackedBarChartRoundedTopBar';
import { StackedTotalsTooltip } from './StackedBarChartTooltip';
import {
  buildStackedBarChartGlobalTooltipStyles,
  buildStackedBarChartTooltipSx,
} from './stackedBarChartTooltipStyles';
import { buildStackedYAxisScale } from './stackedBarChartYAxisScale';
import { estimateStackedBarChartYAxisWidth } from './stackedBarChartYAxisWidth';
import { useRecentChartItemSelection } from './useRecentChartItemSelection';
import { useSyncStackedBarChartHorizontalGridLines } from './useSyncStackedBarChartHorizontalGridLines';
import { MuiBarChart } from './xCharts';
import type { BarItemIdentifier, ChartsAxisData } from '@mui/x-charts/models';

export type { StackedBarChartDatum } from './StackedBarChart.types';

const StackedBarChartBase = ({
  data,
  series,
  height = 260,
  xAxisLabelFormatter = formatChartDateLabel,
  xAxisTickLabelInterval,
  tooltipLabelFormatter = formatChartDateLabel,
  hideXAxisTicks = false,
  showAllXAxisLabels = false,
  xAxisLabelOffset = 4,
  yAxisLabelOffset = -6,
  axisStrokeWidth = 1,
  marginLeft = -8,
  marginRight = 16,
  marginTop = 16,
  marginBottom = 14,
  yAxisWidth = 72,
  yAxisAutoWidth = false,
  yAxisAutoWidthPadding = 12,
  yAxisHeadroomTicks = 0,
  yAxisTopPaddingRatio: _yAxisTopPaddingRatio = 0.12,
  plotAreaBackgroundColor,
  showPlotAreaBackground = true,
  showPlotAreaFrame = false,
  showPlotAreaTopFrame = true,
  categoryGapRatio,
  barGapRatio,
  barCornerRadius = radii.md,
  stackId = 'total',
  skipAnimation = false,
  animateDataChanges = false,
  showHorizontalGrid = true,
  showVerticalGrid = false,
  hideXAxisLine = false,
  hideYAxisLine = false,
  onSelectBar,
  disableInteraction = false,
  disableTooltip = false,
  tooltipEmptyValueLabel = '',
}: StackedBarChartProps): JSX.Element => {
  const resolvedCategoryGapRatio = categoryGapRatio ?? chartBarSizing.default.categoryGapRatio;
  const resolvedBarGapRatio = barGapRatio ?? chartBarSizing.default.barGapRatio;
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [plotBounds, setPlotBounds] = useState<StackedBarChartPlotBounds | null>(null);
  const resizeResetTimerRef = useRef<number | null>(null);
  const hasRenderedNonEmptyDataRef = useRef(false);
  const [suspendAnimationForResize, setSuspendAnimationForResize] = useState(false);
  const [resolvedYAxisWidth, setResolvedYAxisWidth] = useState(yAxisWidth);
  const barRadius = barCornerRadius;
  const { markRecentItemSelection, shouldSuppressAxisSelection } = useRecentChartItemSelection();
  const handleItemClick = useCallback(
    (_event: ReactMouseEvent<SVGElement>, item: BarItemIdentifier) => {
      const dataIndex = item.dataIndex;
      if (typeof dataIndex !== 'number' || !Number.isFinite(dataIndex)) return;
      const seriesKey = item.seriesId ? String(item.seriesId) : undefined;
      markRecentItemSelection(dataIndex);
      onSelectBar?.(dataIndex, seriesKey);
    },
    [markRecentItemSelection, onSelectBar],
  );
  const handleAxisClick = useCallback(
    (_event: MouseEvent, axisData: ChartsAxisData | null) => {
      const target = _event.target;
      if (target && target instanceof Element && target.closest('.MuiBarElement-root')) {
        return;
      }
      const dataIndex = axisData?.dataIndex;
      if (typeof dataIndex !== 'number' || !Number.isFinite(dataIndex)) return;
      if (shouldSuppressAxisSelection(dataIndex)) {
        return;
      }
      onSelectBar?.(dataIndex);
    },
    [onSelectBar, shouldSuppressAxisSelection],
  );
  const palette = useMemo(
    () => [
      uiTokens.statusColors.purple,
      uiTokens.statusColors.green,
      uiTokens.statusColors.orange,
      uiTokens.statusColors.blue,
      uiTokens.statusColors.red,
    ],
    [],
  );
  const chartStatusColors = useMemo(() => new Set(Object.values(uiTokens.statusColors)), []);
  const segmentSeparatorColor = theme.palette.background.paper;
  const segmentSeparatorOpacity = 0.45;
  const segmentSeparatorWidth = 0.75;
  const resolveBarColor = useCallback(
    (base: string): string =>
      theme.palette.mode === 'dark'
        ? darken(base, chartStatusColors.has(base) ? chartStatusDarkenCoefficient : 0.26)
        : base,
    [chartStatusColors, theme.palette.mode],
  );
  const tooltipFontSize = theme.typography.subtitle1.fontSize ?? '15px',
    tooltipLineHeight = theme.typography.subtitle1.lineHeight ?? 1.4,
    tooltipFontWeight = theme.typography.fontWeightRegular ?? 400;
  const rawLabels = useMemo(() => data.map((item) => String(item.label)), [data]);
  const xLabels = useMemo(
    () => rawLabels.map((label) => xAxisLabelFormatter(label)),
    [rawLabels, xAxisLabelFormatter],
  );
  const tooltipLabels = useMemo(
    () => data.map((item) => tooltipLabelFormatter(String(item.label))),
    [data, tooltipLabelFormatter],
  );
  const estimatedXAxisWidth =
    typeof containerWidth === 'number'
      ? Math.max(0, containerWidth - resolvedYAxisWidth - marginRight - Math.abs(marginLeft))
      : null;
  const xTickLabelInterval =
    xAxisTickLabelInterval ??
    buildAdaptiveTickLabelInterval(xLabels.length, showAllXAxisLabels, estimatedXAxisWidth);
  const adjustedMarginLeft = marginLeft + (resolvedYAxisWidth - yAxisWidth);
  const estimatedPlotBounds = useMemo(
    () =>
      buildEstimatedStackedBarChartPlotBounds({
        adjustedMarginLeft,
        containerWidth,
        height,
        marginBottom,
        marginRight,
        marginTop,
        resolvedYAxisWidth,
      }),
    [
      adjustedMarginLeft,
      containerWidth,
      height,
      marginBottom,
      marginRight,
      marginTop,
      resolvedYAxisWidth,
    ],
  );
  const seriesKeys = useMemo(() => series.map((item) => item.key), [series]);
  const stackedValueMax = data.reduce((maxValue, row) => {
    const rowTotal = seriesKeys.reduce((sum, key) => {
      const value = Number(row[key]);
      return Number.isFinite(value) ? sum + value : sum;
    }, 0);
    return rowTotal > maxValue ? rowTotal : maxValue;
  }, 0);
  const { max: yAxisMax, ticks: yAxisTicks } = buildStackedYAxisScale(
    stackedValueMax,
    yAxisHeadroomTicks,
  );
  const formatGuidedYAxisTick = useCallback(
    (value: number | null): string => formatWholeNumber(value),
    [],
  );
  const extraLeftGutter = Math.max(0, -marginLeft);
  const estimatedYAxisWidth = useMemo(
    () =>
      yAxisAutoWidth
        ? estimateStackedBarChartYAxisWidth({
            extraLeftGutter,
            labels: yAxisTicks.map((tick) => formatGuidedYAxisTick(tick)),
            padding: yAxisAutoWidthPadding,
            yAxisLabelOffset,
            yAxisWidth,
          })
        : yAxisWidth,
    [
      extraLeftGutter,
      formatGuidedYAxisTick,
      yAxisAutoWidth,
      yAxisAutoWidthPadding,
      yAxisLabelOffset,
      yAxisTicks,
      yAxisWidth,
    ],
  );
  const { totalsByLabel, tooltipLabelByAxisLabel } = useMemo(() => {
    const nextTotalsByLabel = new Map<string, number>();
    const nextTooltipLabelByAxisLabel = new Map<string, string>();
    data.forEach((row, index) => {
      const axisLabel = xLabels[index];
      const tooltipLabel = tooltipLabels[index];
      if (axisLabel && tooltipLabel) {
        nextTooltipLabelByAxisLabel.set(axisLabel, tooltipLabel);
      }
      const label = tooltipLabel;
      if (!label) return;
      const total = seriesKeys.reduce((sum, key) => {
        const value = Number(row[key]);
        return Number.isFinite(value) ? sum + value : sum;
      }, 0);
      nextTotalsByLabel.set(label, total);
    });
    return {
      totalsByLabel: nextTotalsByLabel,
      tooltipLabelByAxisLabel: nextTooltipLabelByAxisLabel,
    };
  }, [data, seriesKeys, tooltipLabels, xLabels]);
  const topSeriesKeyByRow = useMemo(
    () =>
      data.map((row) => {
        let topKey: string | null = null;
        seriesKeys.forEach((key) => {
          const value = Number(row[key]);
          if (!Number.isFinite(value) || value === 0) return;
          topKey = key;
        });
        return topKey;
      }),
    [data, seriesKeys],
  );
  const resolveSeriesColor = useCallback(
    (item: StackedBarSeries, index: number): string =>
      resolveBarColor(
        item.color ??
          (item.colorKey ? uiTokens.statusColors[item.colorKey] : palette[index % palette.length]),
      ),
    [palette, resolveBarColor],
  );
  const effectiveSkipAnimation =
    skipAnimation ||
    suspendAnimationForResize ||
    (!animateDataChanges && hasRenderedNonEmptyDataRef.current);
  const axisStrokeWidthPx = `${axisStrokeWidth}px`;
  const resolvedPlotAreaBackgroundColor =
    plotAreaBackgroundColor ?? resolveChartPlotAreaBackgroundColor(theme);
  const plotAreaFillBounds = plotBounds ?? estimatedPlotBounds;
  const alignedYAxisLineLeft = plotBounds
    ? Math.max(0, plotBounds.left - axisStrokeWidth / 2)
    : null;
  const alignedRightYAxisLineLeft = plotBounds
    ? plotBounds.left + plotBounds.width - axisStrokeWidth / 2
    : null;
  const alignedTopXAxisLineTop = plotBounds
    ? Math.max(0, plotBounds.top - axisStrokeWidth / 2)
    : null;
  const yAxisTickLabelVisualOffset = yAxisLabelOffset - 4;
  useEffect(() => {
    hasRenderedNonEmptyDataRef.current ||= data.length > 0 && series.length > 0;
  }, [data.length, series.length]);
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const updateWidth = (): void => {
      const nextWidth = container.clientWidth;
      setContainerWidth((currentWidth) => (currentWidth === nextWidth ? currentWidth : nextWidth));
    };
    updateWidth();
    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });
    resizeObserver.observe(container);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  useLayoutEffect(() => {
    if (!yAxisAutoWidth) {
      setResolvedYAxisWidth(yAxisWidth);
      return;
    }
    setResolvedYAxisWidth((currentWidth) =>
      currentWidth < estimatedYAxisWidth ? estimatedYAxisWidth : currentWidth,
    );
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    let frameId = 0;
    const updateYAxisWidth = (): void => {
      const labels = container.querySelectorAll(
        '.MuiChartsAxis-directionY .MuiChartsAxis-tickLabel',
      );
      if (labels.length === 0) return;
      let maxLabelWidth = 0;
      labels.forEach((label) => {
        if (!(label instanceof SVGGraphicsElement)) return;
        const bounds = label.getBBox();
        if (Number.isFinite(bounds.width) && bounds.width > maxLabelWidth) {
          maxLabelWidth = bounds.width;
        }
      });
      const nextWidth = Math.max(
        estimatedYAxisWidth,
        Math.ceil(
          maxLabelWidth + Math.abs(yAxisLabelOffset) + yAxisAutoWidthPadding + extraLeftGutter,
        ),
      );
      setResolvedYAxisWidth((currentWidth) =>
        nextWidth - currentWidth > 1 ? nextWidth : currentWidth,
      );
    };
    const scheduleUpdate = (): void => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(updateYAxisWidth);
    };
    scheduleUpdate();
    const resizeObserver = new ResizeObserver(() => {
      scheduleUpdate();
    });
    resizeObserver.observe(container);
    const svg = container.querySelector('svg');
    if (svg instanceof SVGSVGElement) resizeObserver.observe(svg);
    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      resizeObserver.disconnect();
    };
  }, [
    data,
    estimatedYAxisWidth,
    extraLeftGutter,
    marginLeft,
    series,
    yAxisAutoWidth,
    yAxisAutoWidthPadding,
    yAxisLabelOffset,
    yAxisWidth,
  ]);
  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    let previousWidth = element.clientWidth;
    let previousHeight = element.clientHeight;
    const observer = new ResizeObserver((entries) => {
      const [entry] = entries;
      const width = entry.contentRect.width;
      const heightValue = entry.contentRect.height;
      if (width === previousWidth && heightValue === previousHeight) return;
      previousWidth = width;
      previousHeight = heightValue;
      setSuspendAnimationForResize(true);
      if (resizeResetTimerRef.current !== null) window.clearTimeout(resizeResetTimerRef.current);
      resizeResetTimerRef.current = window.setTimeout(() => {
        setSuspendAnimationForResize(false);
        resizeResetTimerRef.current = null;
      }, 180);
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      if (resizeResetTimerRef.current !== null) {
        window.clearTimeout(resizeResetTimerRef.current);
        resizeResetTimerRef.current = null;
      }
    };
  }, []);
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    let frameId = 0;
    const updatePlotBounds = (): void => {
      const svg = container.querySelector('svg');
      if (!(svg instanceof SVGSVGElement)) return;
      const yAxisLine = container.querySelector(
        '.MuiChartsAxis-directionY .MuiChartsAxis-line',
      ) as SVGGraphicsElement | null;
      const xAxisLine = container.querySelector(
        '.MuiChartsAxis-directionX .MuiChartsAxis-line',
      ) as SVGGraphicsElement | null;
      const xAxisTicks = Array.from(
        container.querySelectorAll('.MuiChartsAxis-directionX .MuiChartsAxis-tick'),
      ).filter((node): node is SVGGraphicsElement => node instanceof SVGGraphicsElement);
      const yAxisTickLabels = Array.from(
        container.querySelectorAll('.MuiChartsAxis-directionY .MuiChartsAxis-tickLabel'),
      ).filter((node): node is SVGGraphicsElement => node instanceof SVGGraphicsElement);
      const nextPlotBounds = calculateChartPlotBounds({
        marginTop,
        svgRect: svg.getBoundingClientRect(),
        xAxisRect: xAxisLine?.getBoundingClientRect() ?? null,
        xAxisTickRects: xAxisTicks.map((tick) => tick.getBoundingClientRect()),
        yAxisRect: yAxisLine?.getBoundingClientRect(),
        yAxisTickLabelRects: yAxisTickLabels.map((label) => label.getBoundingClientRect()),
      });
      setPlotBounds((currentBounds) => {
        if (nextPlotBounds === null) {
          return currentBounds;
        }
        if (
          currentBounds !== null &&
          currentBounds.left === nextPlotBounds.left &&
          currentBounds.top === nextPlotBounds.top &&
          currentBounds.width === nextPlotBounds.width &&
          currentBounds.height === nextPlotBounds.height
        ) {
          return currentBounds;
        }
        return nextPlotBounds;
      });
    };
    const scheduleUpdate = (): void => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(updatePlotBounds);
    };
    scheduleUpdate();
    const resizeObserver = new ResizeObserver(() => {
      scheduleUpdate();
    });
    resizeObserver.observe(container);
    const svg = container.querySelector('svg');
    if (svg instanceof SVGSVGElement) {
      resizeObserver.observe(svg);
    }
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [data, height, marginTop, resolvedYAxisWidth, series, yAxisWidth]);
  useSyncStackedBarChartHorizontalGridLines({ containerRef, showHorizontalGrid });
  const chartSeries = useMemo(
    () =>
      series.map((item, index) => ({
        id: item.key,
        data: data.map((row) => Number(row[item.key])),
        label: item.label,
        color: resolveSeriesColor(item, index),
        stack: stackId,
        valueFormatter: (value: number | null) => formatStackedBarTooltipValue(value),
      })),
    [data, resolveSeriesColor, series, stackId],
  );
  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'transparent',
        color: theme.app.colors.divider,
        '& .MuiChartsSurface-root': {
          backgroundColor: 'transparent !important',
          color: theme.app.colors.divider,
          overflow: 'visible',
        },
        '& .MuiChartsSurface-root svg': {
          backgroundColor: 'transparent !important',
          overflow: 'visible',
        },
      }}
    >
      {showPlotAreaBackground && plotAreaFillBounds ? (
        <Box
          sx={buildChartPlotAreaFillSx({
            backgroundColor: resolvedPlotAreaBackgroundColor,
            inset: 0,
            plotBounds: plotAreaFillBounds,
          })}
          aria-hidden="true"
        />
      ) : null}
      {showPlotAreaFrame && hideYAxisLine && plotBounds && alignedYAxisLineLeft !== null ? (
        <Box
          data-chart-axis="left"
          sx={{
            position: 'absolute',
            left: alignedYAxisLineLeft,
            top: plotBounds.top,
            width: axisStrokeWidthPx,
            height: plotBounds.height,
            backgroundColor: theme.app.colors.divider,
            pointerEvents: 'none',
            zIndex: 2,
          }}
          aria-hidden="true"
        />
      ) : null}
      {showPlotAreaFrame && plotBounds && alignedRightYAxisLineLeft !== null ? (
        <Box
          data-chart-axis="right"
          sx={{
            position: 'absolute',
            left: alignedRightYAxisLineLeft,
            top: plotBounds.top,
            width: axisStrokeWidthPx,
            height: plotBounds.height,
            backgroundColor: theme.app.colors.divider,
            pointerEvents: 'none',
            zIndex: 2,
          }}
          aria-hidden="true"
        />
      ) : null}
      {showPlotAreaTopFrame && plotBounds && alignedTopXAxisLineTop !== null ? (
        <Box
          data-chart-axis="top"
          sx={{
            position: 'absolute',
            left: plotBounds.left,
            top: alignedTopXAxisLineTop,
            width: plotBounds.width,
            height: axisStrokeWidthPx,
            backgroundColor: theme.app.colors.divider,
            pointerEvents: 'none',
            zIndex: 2,
          }}
          aria-hidden="true"
        />
      ) : null}
      {!hideYAxisLine && plotBounds && alignedYAxisLineLeft !== null ? (
        <Box
          data-chart-axis="y"
          sx={{
            position: 'absolute',
            left: alignedYAxisLineLeft,
            top: plotBounds.top,
            width: axisStrokeWidthPx,
            height: plotBounds.height,
            backgroundColor: theme.app.colors.divider,
            pointerEvents: 'none',
            zIndex: 2,
          }}
          aria-hidden="true"
        />
      ) : null}
      <GlobalStyles styles={buildStackedBarChartGlobalTooltipStyles(theme)} />
      <MuiBarChart
        height={height}
        hideLegend
        skipAnimation={effectiveSkipAnimation}
        borderRadius={barRadius}
        slots={{
          bar: (props) => (
            <StackedBarChartRoundedTopBar
              {...props}
              topSeriesKeyByRow={topSeriesKeyByRow}
              barRadius={barRadius}
              segmentSeparatorColor={segmentSeparatorColor}
              segmentSeparatorOpacity={segmentSeparatorOpacity}
              segmentSeparatorWidth={segmentSeparatorWidth}
            />
          ),
          tooltip: (tooltipProps) => (
            <StackedTotalsTooltip
              {...tooltipProps}
              totalsByLabel={totalsByLabel}
              tooltipLabelByAxisLabel={tooltipLabelByAxisLabel}
              tooltipLabelsByIndex={tooltipLabels}
              tooltipLabelFormatter={tooltipLabelFormatter}
              tooltipEmptyValueLabel={tooltipEmptyValueLabel}
            />
          ),
        }}
        onItemClick={onSelectBar && !disableInteraction ? handleItemClick : undefined}
        onAxisClick={onSelectBar && !disableInteraction ? handleAxisClick : undefined}
        xAxis={[
          {
            data: rawLabels,
            scaleType: 'band',
            categoryGapRatio: resolvedCategoryGapRatio,
            barGapRatio: resolvedBarGapRatio,
            tickPlacement: 'extremities',
            tickLabelPlacement: 'middle',
            valueFormatter: (value) => xAxisLabelFormatter(String(value)),
            tickLabelInterval: xTickLabelInterval,
            tickLabelStyle: {
              fill: theme.app.colors.textMuted,
              fontSize: '13px',
              transform: `translateY(${xAxisLabelOffset}px)`,
            },
            tickSize: hideXAxisTicks ? 0 : 6,
          },
        ]}
        yAxis={[
          {
            min: 0,
            max: yAxisMax > 0 ? yAxisMax : undefined,
            width: resolvedYAxisWidth,
            tickLabelStyle: {
              fill: theme.app.colors.textMuted,
              fontSize: '13px',
              transform: `translateX(${yAxisLabelOffset}px)`,
            },
            tickSize: 0,
            tickInterval: yAxisTicks,
            tickNumber: guidedYAxisTickNumber,
            valueFormatter: formatGuidedYAxisTick,
          },
        ]}
        grid={{ horizontal: showHorizontalGrid, vertical: showVerticalGrid }}
        margin={{
          left: adjustedMarginLeft,
          right: marginRight,
          top: marginTop,
          bottom: marginBottom,
        }}
        slotProps={{
          axisLine: { stroke: theme.app.colors.divider },
          axisTick: { stroke: theme.app.colors.divider },
          tooltip: {
            classes: {
              paper: 'flowqa-stacked-tooltip-paper',
              table: 'flowqa-stacked-tooltip-table',
              cell: 'flowqa-stacked-tooltip-cell',
              mark: 'flowqa-stacked-tooltip-mark',
            },
            sx: buildStackedBarChartTooltipSx({
              borderColor: theme.app.colors.border,
              textColor: theme.app.colors.text,
              tooltipFontSize,
              tooltipFontWeight,
              tooltipLineHeight,
            }),
          },
        }}
        sx={{
          position: 'relative',
          zIndex: 1,
          '& .MuiChartsAxis-tickLabel': { fill: theme.app.colors.textMuted },
          '& .MuiChartsAxis-directionY .MuiChartsAxis-tickLabel': {
            transform: `translateX(${yAxisTickLabelVisualOffset}px) !important`,
          },
          '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
            stroke: theme.app.colors.divider,
            strokeOpacity: 1,
            strokeWidth: axisStrokeWidth,
          },
          '& .MuiChartsGrid-horizontalLine': {
            stroke: theme.app.colors.divider,
            strokeWidth: '0.75px',
          },
          '& .MuiChartsAxis-line': {
            stroke: theme.app.colors.divider,
            strokeOpacity: 1,
            strokeWidth: axisStrokeWidth,
          },
          '& .MuiChartsAxis-line[stroke]': {
            stroke: `${theme.app.colors.divider} !important`,
            strokeWidth: `${axisStrokeWidth}px !important`,
          },
          '& .MuiChartsAxis-tick[stroke]': {
            stroke: `${theme.app.colors.divider} !important`,
            strokeWidth: `${axisStrokeWidth}px !important`,
          },
          ...buildStackedBarChartAxisLineSx({
            dividerColor: theme.app.colors.divider,
            hideXAxisLine,
            hideYAxisLine: true,
          }),
          ...(onSelectBar && !disableInteraction
            ? {
                '& .MuiChartsAxis-directionX': { cursor: 'pointer' },
                '& .MuiChartsAxis-directionX .MuiChartsAxis-tickLabel': { cursor: 'pointer' },
                '& .MuiChartsAxis-directionX .MuiChartsAxis-tick': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-root': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-root *': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight *': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-line, & .MuiChartsAxisHighlight-rect': {
                  cursor: 'pointer',
                },
                '& .MuiChartsAxisHighlightLine, & .MuiChartsAxisHighlightRect': {
                  cursor: 'pointer',
                },
                '& .MuiChartsOverlay-root': { cursor: 'pointer' },
                '& svg': { cursor: 'pointer' },
              }
            : null),
          ...(disableInteraction
            ? {
                '& .MuiChartsOverlay-root': { pointerEvents: 'none' },
                '& .MuiChartsAxisHighlight-root': { pointerEvents: 'none' },
                '& .MuiChartsAxisHighlight': { pointerEvents: 'none' },
                '& .MuiBarElement-root': { pointerEvents: 'none' },
              }
            : null),
          ...(disableTooltip
            ? {
                '& .MuiChartsTooltip-root': { display: 'none' },
                '& .MuiChartsTooltip-paper': { display: 'none' },
              }
            : null),
        }}
        series={chartSeries}
      />
    </Box>
  );
};
export const StackedBarChart = memo(StackedBarChartBase);
